import React, { FC, memo, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as styles from './VideoOverlay.module.css';
import closeButton from 'url:../../assets/images/cross-black-icon.webp';

type Props = {
  open: boolean;
  onClose: () => void;
  video?: string;
};

const VideoOverlay: FC<Props> = ({ open, onClose, video }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [mounted, setMounted] = useState(false);
  const [phase, setPhase] = useState<'closed' | 'open' | 'closing'>('closed');

  /* ---------- OPEN (ESLint-safe) ---------- */
  useLayoutEffect(() => {
    if (!open) return;

    let raf2: number;

    const raf1 = requestAnimationFrame(() => {
      setMounted(true);
      setPhase('closed');

      raf2 = requestAnimationFrame(() => {
        setPhase('open');
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [open]);

  /* ---------- CLOSE ---------- */
  const handleClose = () => {
    setPhase('closing');

    setTimeout(() => {
      setMounted(false);
      setPhase('closed');
      onClose();
    }, 280); // must match CSS transition
  };

  /* ---------- VIDEO + SCROLL LOCK ---------- */
  useLayoutEffect(() => {
    if (!mounted || phase !== 'open') return;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    const v = videoRef.current;
    if (v) {
      requestAnimationFrame(() => {
        v.currentTime = 0;
        v.play().catch(() => {});
      });
    }

    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [mounted, phase]);

  if (!mounted) return null;

  return createPortal(
    <div
      className={`${styles.overlay} ${
        phase === 'open' ? styles.open : phase === 'closing' ? styles.closing : ''
      }`}
    >
      <div className={styles.backdrop} onClick={handleClose} />

      <div className={styles.container}>
        <div className={styles.videoContainer}>
          <div className={styles.closeButton} onClick={handleClose} role="button">
            <img className={styles.closeIcon} src={closeButton} alt="Close" />
          </div>

          <video ref={videoRef} src={video} controls playsInline />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default memo(VideoOverlay);
